import React, { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import Layout from '../Layout';
import { Card, CardHeader, CardBody, Button, Table, Input, Row, Col } from 'reactstrap';
import EditLeagueModal from './EditLeagueModal';
import ClearEventsModal from './ClearEventsModal';
import ScheduleGenerationModal from './ScheduleGenerationModal';

const LeagueManagement = () => {
  const [leagues, setLeagues] = useState([]);
  const [showArchivedLeagues, setShowArchivedLeagues] = useState(false);
  const [services, setServices] = useState([]);
  const [teamCounts, setTeamCounts] = useState({});
  const [newLeague, setNewLeague] = useState({
    title: '',
    description: '',
    service_id: '',
    state: 'New',
    sort_order: 99,
    lock_resources: false
  });
  const [editLeagueModal, setEditLeagueModal] = useState(false);
  const [editingLeague, setEditingLeague] = useState(null);
  
  // Clear events confirmation modal state
  const [clearEventsModal, setClearEventsModal] = useState({
    isOpen: false,
    leagueId: null,
    leagueName: '',
    isClearing: false
  });
  
  // Schedule generation modal state
  const [scheduleModal, setScheduleModal] = useState({
    isOpen: false,
    taskId: null,
    status: null,
    message: '',
    output: '',
    errors: '',
    orgName: '',
    leagueName: '',
    createdAt: null,
    startedAt: null,
    completedAt: null,
    failedAt: null
  });
  
  const pollingIntervalRef = useRef(null);

  useEffect(() => {
    fetchLeagues();
    fetchServices();
    
    // Cleanup polling on unmount
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  const fetchLeagues = async () => {
    try {
      const response = await api.get('/leagues/');
      setLeagues(response.data);
      
      // Fetch team counts for all leagues
      const counts = {};
      await Promise.all(
        response.data.map(async (league) => {
          try {
            const teamsResponse = await api.get(`/league-teams/?league=${league.id}`);
            counts[league.id] = teamsResponse.data.length;
          } catch (error) {
            console.error(`Error fetching team count for league ${league.id}:`, error);
            counts[league.id] = 0;
          }
        })
      );
      setTeamCounts(counts);
    } catch (error) {
      console.error('Error fetching leagues:', error);
    }
  };

  const fetchServices = async () => {
    try {
      const response = await api.get('/services/');
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const addLeague = async () => {
    try {
      await api.post('/leagues/', newLeague);
      fetchLeagues();
      setNewLeague({ title: '', description: '', service_id: '', state: 'New', sort_order: 99, lock_resources: false });
    } catch (error) {
      console.error('Error adding league:', error);
    }
  };

  /*   const deleteLeague = async (id) => {
      try {
        await api.delete(`/leagues/${id}/`);
        fetchLeagues();
      } catch (error) {
        console.error('Error deleting league:', error);
      }
    };
   */
  const handleEditLeague = (league) => {
    setEditingLeague(league);
    setEditLeagueModal(true);
  };

  const handleSaveLeague = async (leagueData) => {
    try {
      await api.put(`/leagues/${editingLeague.id}/`, leagueData);
      setEditLeagueModal(false);
      setEditingLeague(null);
      fetchLeagues();
    } catch (error) {
      console.error('Error saving league:', error);
      alert('Error saving league: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleExport = async (leagueId) => {
    try {
      const response = await api.get(`/export/league-events/${leagueId}/`, {
        responseType: 'blob',
      });

      const disposition = response.headers['content-disposition'];
      let filename = `league_${leagueId}_events.csv`;

      if (disposition && disposition.includes('filename=')) {
        filename = disposition.split('filename=')[1].replace(/['"]/g, '');
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting events:', error);
    }
  };

  const openScheduleModal = (league) => {
    // Open modal in 'ready' state, waiting for user to click Start
    setScheduleModal({
      isOpen: true,
      taskId: null,
      status: 'ready',
      message: 'Ready to generate schedule. Click Start to begin.',
      output: '',
      errors: '',
      orgName: 'Stingers',
      leagueName: league.title,
      createdAt: new Date().toISOString(),
      startedAt: null,
      completedAt: null,
      failedAt: null,
      league: league // Store league reference for later
    });
  };

  const handleClearEvents = (league) => {
    setClearEventsModal({
      isOpen: true,
      leagueId: league.id,
      leagueName: league.title,
      isClearing: false
    });
  };

  const confirmClearEvents = async () => {
    const leagueId = clearEventsModal.leagueId;
    
    setClearEventsModal(prev => ({ ...prev, isClearing: true }));

    try {
      await api.delete(`/leagues/${leagueId}/events/`);
      setClearEventsModal({
        isOpen: false,
        leagueId: null,
        leagueName: '',
        isClearing: false
      });
      fetchLeagues();
    } catch (error) {
      console.error('Error clearing events:', error);
      setClearEventsModal(prev => ({ ...prev, isClearing: false }));
      alert('Error clearing events: ' + (error.response?.data?.detail || error.message));
    }
  };

  const cancelClearEvents = () => {
    setClearEventsModal({
      isOpen: false,
      leagueId: null,
      leagueName: '',
      isClearing: false
    });
  };

  const startScheduleGeneration = async () => {
    const league = scheduleModal.league;
    if (!league) return;

    try {
      // Update modal to show we're starting
      setScheduleModal(prev => ({
        ...prev,
        status: 'pending',
        message: 'Initiating schedule generation...'
      }));

      const response = await api.post('/league-schedule/generate/', {
        org_name: 'Stingers',
        league_name: league.title
      });
      
      // Update modal with task info and start polling
      const taskId = response.data.task_id;
      setScheduleModal(prev => ({
        ...prev,
        taskId: taskId,
        status: 'pending',
        message: 'Schedule generation initiated...',
        orgName: response.data.org_name || 'Stingers',
        leagueName: response.data.league_name || league.title,
        createdAt: response.data.created_at || new Date().toISOString()
      }));
      
      // Start polling for status
      startPolling(taskId);
    } catch (error) {
      console.error('Error generating schedule:', error);
      setScheduleModal(prev => ({
        ...prev,
        taskId: null,
        status: 'failed',
        message: 'Error generating schedule',
        errors: error.response?.data?.detail || error.message || 'Unknown error occurred',
        failedAt: new Date().toISOString()
      }));
    }
  };
  
  const startPolling = (taskId) => {
    // Clear any existing polling
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    
    // Poll every 2 seconds
    pollingIntervalRef.current = setInterval(() => {
      checkScheduleStatus(taskId);
    }, 2000);
    
    // Also check immediately
    checkScheduleStatus(taskId);
  };
  
  const checkScheduleStatus = async (taskId) => {
    try {
      const response = await api.get(`/league-schedule/status/${taskId}/`);
      const data = response.data;
      
      setScheduleModal(prev => ({
        ...prev,
        status: data.status,
        message: data.message || '',
        output: data.output || '',
        errors: data.errors || '',
        orgName: data.org_name || prev.orgName,
        leagueName: data.league_name || prev.leagueName,
        createdAt: data.created_at || prev.createdAt,
        startedAt: data.started_at || prev.startedAt,
        completedAt: data.completed_at || prev.completedAt,
        failedAt: data.failed_at || prev.failedAt
      }));
      
      // Stop polling if job is complete
      if (['completed', 'completed_with_errors', 'failed'].includes(data.status)) {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        
        // Refresh leagues when completed
        if (data.status === 'completed' || data.status === 'completed_with_errors') {
          fetchLeagues();
        }
      }
    } catch (error) {
      console.error('Error checking schedule status:', error);
      // On error, stop polling and update modal
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      
      setScheduleModal(prev => ({
        ...prev,
        status: 'failed',
        message: 'Error checking status',
        errors: error.response?.data?.detail || error.message || 'Failed to check status'
      }));
    }
  };
  
  const closeScheduleModal = () => {
    // Stop polling when closing
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    
    setScheduleModal({
      isOpen: false,
      taskId: null,
      status: null,
      message: '',
      output: '',
      errors: '',
      orgName: '',
      leagueName: '',
      createdAt: null,
      startedAt: null,
      completedAt: null,
      failedAt: null
    });
  };

  const displayedLeagues = showArchivedLeagues
    ? leagues
    : leagues.filter((league) => league.state !== 'Archived');

  return (
    <Layout>
      <div style={{ padding: '1rem' }}>
        {/* --- League List Card --- */}
        <Card className="mb-4">
          <CardHeader tag="h3">League Management</CardHeader>
          <CardBody>
            <Table bordered hover striped responsive>
              <thead>
                <tr>
                  <th>&nbsp;</th>
                  <th>League</th>
                  <th>Links</th>
                  <th>Teams</th>
                  <th>State</th>
                  <th>Sort</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayedLeagues.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center">
                      {showArchivedLeagues ? 'No leagues found' : 'No active leagues found'}
                    </td>
                  </tr>
                ) : (
                  displayedLeagues.map((league) => (
                    <React.Fragment key={league.id}>
                      <tr>
                        <td>
                          <Button color="primary" size="sm" onClick={() => handleEditLeague(league)}>Edit</Button>
                        </td>
                        <td>
                          <div>{league.title}</div>
                        </td>
                        <td className="text-center">
                          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                            {league.state === 'Registration' && (
                              <Button
                                color="link"
                                size="sm"
                                onClick={() => window.open(`/league/${league.guid}/register`, '_blank')}
                              >
                                Registration
                              </Button>
                            )}
                            <Button
                              color="link"
                              size="sm"
                              onClick={() => window.open(`../league/${league.guid}/schedule`, '_blank')}
                            >
                              Schedule
                            </Button>
                            {league.show_leaderboard && (league.state === 'Registration' || league.state === 'Active') && (
                              <Button
                                color="link"
                                size="sm"
                                onClick={() => window.open(`/league/${league.guid}/scores`, '_blank')}
                              >
                                Leaderboard
                              </Button>
                            )}
                            {league.scoring_link && (
                              <Button
                                color="link"
                                size="sm"
                                onClick={() => window.open(league.scoring_link, '_blank')}
                              >
                                Scoring
                              </Button>
                            )}
                          </div>
                        </td>
                        <td className="text-center">
                          {teamCounts[league.id] !== undefined ? teamCounts[league.id] : '...'}
                        </td>
                        <td>
                          <span className={`badge bg-${
                            league.state === 'Active' ? 'success' : 
                            league.state === 'Registration' ? 'primary' : 
                            league.state === 'New' ? 'secondary' : 
                            'dark'
                          }`}>
                            {league.state || 'New'}
                          </span>
                        </td>
                        <td>
                          {league.sort_order || 99}
                        </td>
                        <td>
                          <Button color="success" size="sm" onClick={() => handleExport(league.id)}>Export Events</Button>{' '}
                          {league.state === 'Registration' && (
                            <Button color="warning" size="sm" onClick={() => openScheduleModal(league)}>Create Schedule</Button>
                          )}{' '}
                          {league.state === 'New' && (
                            <Button color="danger" size="sm" onClick={() => handleClearEvents(league)}>Clear Events</Button>
                          )}
                        </td>
                      </tr>
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </Table>
            <div className="form-check mt-3">
              <Input
                type="checkbox"
                id="showArchivedLeagues"
                checked={showArchivedLeagues}
                onChange={(e) => setShowArchivedLeagues(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="showArchivedLeagues">
                Show Archived Leagues
              </label>
            </div>
          </CardBody>
        </Card>

        {/* --- Add League Card --- */}
        <Card>
          <CardHeader tag="h4">Add New League</CardHeader>
          <CardBody>
            <Row>
              <Col md={2}>
                <Input
                  type="text"
                  value={newLeague.title}
                  onChange={(e) => setNewLeague((prev) => ({ ...prev, title: e.target.value }))}
                  placeholder="League title"
                />
              </Col>
              <Col md={4}>
                <Input
                  type="textarea"
                  value={newLeague.description}
                  onChange={(e) => setNewLeague((prev) => ({ ...prev, description: e.target.value }))}
                  placeholder="League description"
                />
              </Col>
              <Col md={3}>
                <Input
                  type="select"
                  value={newLeague.service_id}
                  onChange={(e) => setNewLeague((prev) => ({ ...prev, service_id: e.target.value }))}
                >
                  <option value="">-- Select Service --</option>
                  {services.map((svc) => (
                    <option key={svc.id} value={svc.id}>
                      {svc.title} ({svc.duration} min)
                    </option>
                  ))}
                </Input>
              </Col>
              <Col md={1}>
                <Input
                  type="number"
                  value={newLeague.sort_order}
                  onChange={(e) => setNewLeague((prev) => ({ ...prev, sort_order: parseInt(e.target.value) || 99 }))}
                  placeholder="Sort"
                />
              </Col>
              <Col md={2} className="d-flex align-items-center">
                <div className="form-check">
                  <Input
                    type="checkbox"
                    id="newLeagueLockResources"
                    checked={newLeague.lock_resources}
                    onChange={(e) => setNewLeague((prev) => ({ ...prev, lock_resources: e.target.checked }))}
                  />
                  <label className="form-check-label" htmlFor="newLeagueLockResources">
                    Lock resources
                  </label>
                </div>
              </Col>
              <Col md={2} className="d-flex align-items-center">
                <Button color="primary" onClick={addLeague} block>
                  Add League
                </Button>
              </Col>
            </Row>
          </CardBody>
        </Card>
        
        <ClearEventsModal
          isOpen={clearEventsModal.isOpen}
          leagueName={clearEventsModal.leagueName}
          isClearing={clearEventsModal.isClearing}
          onConfirm={confirmClearEvents}
          onCancel={cancelClearEvents}
        />
        
        <ScheduleGenerationModal
          isOpen={scheduleModal.isOpen}
          taskId={scheduleModal.taskId}
          status={scheduleModal.status}
          message={scheduleModal.message}
          output={scheduleModal.output}
          errors={scheduleModal.errors}
          orgName={scheduleModal.orgName}
          leagueName={scheduleModal.leagueName}
          createdAt={scheduleModal.createdAt}
          startedAt={scheduleModal.startedAt}
          completedAt={scheduleModal.completedAt}
          failedAt={scheduleModal.failedAt}
          onStart={startScheduleGeneration}
          onClose={closeScheduleModal}
        />

        <EditLeagueModal
          isOpen={editLeagueModal}
          toggle={() => setEditLeagueModal(false)}
          onSave={handleSaveLeague}
          league={editingLeague}
          services={services}
        />
      </div>
    </Layout>
  );
};

export default LeagueManagement;
